import { FC, useContext } from 'react';
import { KPIMetricBox } from './kpi-metric-box';
import { Grid, Typography } from '@mui/material';
import { useQuery } from 'react-query';
import { getSalesDashboardMetrics } from '../../../fetch/sales';
import { Loader } from '../../../components';
import { useFlags } from '../../../hooks';
import { UserContext } from '../../../context';
import { UserRoles } from '../../../models';
import { contractingLinks, salesToolsLinks } from '../../../helpers';

type IMetric = {
  name: string;
  count: number;
};

interface IKPIMetricsContainerContext {}

// Combine the links from contracting and sales tools.
const links = [...contractingLinks, ...salesToolsLinks];

// Find the links by id so changes to the links don't break the metrics' links.
const MetricLinks = {
  'New Contracts': links.find(link => link.id === 'contract-acceptance').url,
  'Ten Day Builds': links.find(link => link.id === 'ten-day-builds').url,
  'Test Builds': links.find(link => link.id === 'test-builds').url,
  'Ten Day Completed': links.find(link => link.id === 'contract-acceptance').url,
  'Under Contract': links.find(link => link.id === 'under-contract').url,
  'Active Spec Homes': links.find(link => link.id === 'view-specs').url,
  'Closing Date Scheduled': links.find(link => link.id === 'closing-calendar').url
};

const MetricFlags = {
  'Test Builds': 'salesTestBuild',
  'Under Contract': 'underContract',
  'Closing Date Scheduled': 'salesClosingCalendar'
};

export const KPIMetricsContainer: FC<IKPIMetricsContainerContext> = () => {
  const featureFlags = useFlags();
  const { user } = useContext(UserContext);

  const hasAccessToClosingDate =
    user?.userRoles?.includes(UserRoles.BLAdmin) ||
    user?.userRoles?.includes(UserRoles.BLSalesAdmin) ||
    user?.userRoles?.includes(UserRoles.BLSalesCoordinator);

  const { isLoading, data: buildMetrics } = useQuery<IMetric[], Error>(
    ['getSalesDashboardMetrics'],
    () => getSalesDashboardMetrics()
  );

  const isActiveMetric = (metric: string) => {
    // If the metric doesn't have a flag defined, it should always be active
    if (!MetricFlags[metric]) {
      return true;
    }

    let metricIsActive = false;
    if (featureFlags[MetricFlags[metric]]) {
      metricIsActive = true;
    }
    if (!hasAccessToClosingDate && metric === 'Closing Date Scheduled') {
      metricIsActive = false;
    }
    return metricIsActive;
  };
  if (isLoading) {
    return <Loader position="centered" />;
  }
  return (
    <Grid spacing={2} container alignItems="center" sx={{ marginTop: 0 }}>
      {buildMetrics?.map(metric => {
        return (
          <Grid key={`${metric.name}-${metric.count}`} xs={12} sm={6} md={5} lg={3} item>
            <KPIMetricBox
              name={metric.name}
              metric={metric.count}
              link={MetricLinks[metric.name]}
              isActive={isActiveMetric(metric.name)}
            />
          </Grid>
        );
      })}
      {!isLoading && buildMetrics?.length === 0 && (
        <Typography variant="h3">No Metrics to display</Typography>
      )}
    </Grid>
  );
};
